import { computed, reactive, readonly, ref, unref } from '@vue/composition-api';
import { useQuery } from 'vue-query';
import { RawLocation, Route } from 'vue-router';
import {
  AtoneAuthenticateGetResponseMemberTypeEnum,
  // AtoneAuthenticateGetResponseSexDivisionEnum,
  AtoneMemberTypePostResponseMemberTypeEnum,
  AtoneRequestTokenPostRequestSexDivisionEnum,
  AtoneTracksPostRequestAuthenticationTypeEnum,
} from '@netprotections/berlin-bff';
import { useContext } from '@berlin-front/common/composables/context';
import { useSessionId } from '@berlin-front/common/composables/useSessionId';
import useFingerprint from '@berlin-front/common/composables/useFingerprint';
import { useRouter } from '@berlin-front/common/composables/useRouter';
import useOrAll from '@berlin-front/common/composables/useOrAll';
// import { event, set } from 'vue-gtag';
import { BerlinBffContext } from '@/services/BerlinBff';
import { ApiCacheContext } from '@/services/ApiCacheContext';
import { getRefDeep, notNil, pick } from '@berlin-front/common/utils';
import { ROUTE_NAME } from '@/router/route-name';
import { ModalBridgeContext } from '@services/ModalBridgeContext';

export const useLoading = (_$route: Route) => ({
  useLoading: () => {
    const router = useRouter();
    const {
      atoneAuthModalParameter,
      setAtoneRequestTokenPostResult,
      tokens,
      setAtoneMemberTypePostResult,
      setAtoneTracksPostResult,
      setAtoneAuthenticateGetResult,
      atoneAuthenticateGetResult,
      setAtoneTracksTrustResult,
      atoneTracksPostResult,
      setAtoneSmsNotifyPostResult,
      atoneTracksTrustResult,
      setNpTokenAndSignature,
    } = useContext(ApiCacheContext);
    const { requestToken, authenticate, member, tracks, sms, payment } = useContext(BerlinBffContext);
    const globals = useContext(ModalBridgeContext);

    const fingerprint = useFingerprint();
    const nextPage = ref<RawLocation | undefined>();
    const memberIsGuest = computed(
      () => atoneAuthenticateGetResult.value?.memberType === AtoneAuthenticateGetResponseMemberTypeEnum.NUMBER_3,
    );
    // リクエストトークン発行API
    const {
      //
      isSuccess: successAtoneRequestTokenPost,
    } = useQuery(
      // 条件変更時のリクエスト実行は行わないため、クエリキーは定数で良い。
      'atone.RequestTokenPost',
      // stateにパラメタセットして、そっから始めるのがuseQueryの作法。
      () =>
        Promise.all([
          requestToken.atoneRequestTokenControllerRequestTokenPost({
            shopPublicKey: atoneAuthModalParameter.value.pubKey,
            mailAddress: atoneAuthModalParameter.value.payment.customer?.email,
            familyName: atoneAuthModalParameter.value.payment.customer?.customerFamilyName,
            familyNameKana: atoneAuthModalParameter.value.payment.customer?.customerFamilyNameKana,
            givenName: atoneAuthModalParameter.value.payment.customer?.customerGivenName,
            givenNameKana: atoneAuthModalParameter.value.payment.customer?.customerGivenNameKana,
            // TODO: Enum調整してasとる。
            sexDivision: atoneAuthModalParameter.value.payment.customer
              ?.sexDivision as unknown as AtoneRequestTokenPostRequestSexDivisionEnum,
            birthday: atoneAuthModalParameter.value.payment.customer?.birthday,
            zipCode: atoneAuthModalParameter.value.payment.customer?.zipCode,
            address: atoneAuthModalParameter.value.payment.customer?.address,
            phoneNumber: atoneAuthModalParameter.value.payment.customer?.phoneNumber,
          }),
          payment.atonePaymentControllerParameterPost(
            atoneAuthModalParameter.value.pubKey,
            {
              requestId: globals.requestId,
              serviceType: '01',
              modalMode: '02',
              ...pick(
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                atoneAuthModalParameter.value.payment as unknown as any,
                'amount',
                'checksum',
                'customer',
                'description',
                'destCustomers',
                'items',
                'salesForm',
                'salesSettled',
                'serviceSupplier',
                'shopTransactionNo',
                'sourceMedia',
                'transactionOptions',
                'userNo',
              ),
            },
            atoneAuthModalParameter.value.terminalId,
          ),
        ] as const).then((v) => v[0]),
      {
        onSuccess: ({ data }) => {
          setAtoneRequestTokenPostResult(data);
        },
      },
    );

    // 認証済API。加盟店からpreTokenを受け取ってる場合、ここのリクエストでログイン状態になる。
    const { isSuccess: successAtoneAuthenticateGet, isError: errorAtoneAuthenticateGet } = useQuery(
      'atone.authenticateGet',
      () =>
        authenticate.atoneAuthenticateControllerAuthenticateGet(
          getRefDeep(tokens, 'npToken') ?? '',
          atoneAuthModalParameter.value.pubKey,
          useSessionId(),
          globals.requestId,
        ),
      {
        enabled: unref(getRefDeep(tokens, 'npToken') ?? '').length > 0,
        onSuccess: ({ data }) => {
          setAtoneAuthenticateGetResult(data);
          setNpTokenAndSignature(tokens.value?.npToken, data.signature);
          // TODO: 仮会員の場合はアップグレードフローに進む。
          if (data.memberType !== AtoneAuthenticateGetResponseMemberTypeEnum.NUMBER_3) {
            window.newrelic.addPageAction('authenticate_by_token', {
              member_id: notNil(data.memberCode),
              shop_id: notNil(data.shopId),
              shop_name: notNil(data.shopName),
              gaid: notNil(data.hashId),
            });
          }

          // TODO: googleTagの挙動はこれでいいか。
          // let gender: string | null;
          // // eslint-disable-next-line no-underscore-dangle
          // if (data.sexDivision === AtoneAuthenticateGetResponseSexDivisionEnum._1) {
          //   gender = '男性';
          //   // eslint-disable-next-line no-underscore-dangle
          // } else if (data.sexDivision === AtoneAuthenticateGetResponseSexDivisionEnum._2) {
          //   gender = '女性';
          // } else {
          //   gender = '不明';
          // }

          // set({
          //   user_id: notNil(data.memberCode),
          //   user_properties: {
          //     member_id: notNil(data.memberCode),
          //     gender,
          //     age: data.age,
          //     shop_id: notNil(data.shopId),
          //     // shop_name: notNil(data.shopName),
          //     guest: data.memberType === AtoneAuthenticateGetResponseMemberTypeEnum.NUMBER_3 ? 'yes' : 'no',
          //   },
          // });

          // event('login', {
          //   method: 'Token',
          // });
        },
      },
    );

    /**
     * 会員判定API。実行条件となる認証トークンの分岐は下記
     * 1. 加盟店からpreTokenを受け取っていない
     * 2. preTokenが有効でない
     * 3. preTokenが有効だが仮会員
     */
    const needLogin = computed(
      () =>
        unref(successAtoneRequestTokenPost) &&
        (unref(getRefDeep(tokens, 'npToken') ?? '').length === 0 ||
          (unref(getRefDeep(tokens, 'npToken') ?? '').length > 0 && unref(errorAtoneAuthenticateGet)) ||
          (unref(successAtoneAuthenticateGet) && unref(memberIsGuest))),
    );
    const { isSuccess: successAtoneMemberTypePost } = useQuery(
      'atone.MemberTypePost',
      async () => {
        if (atoneAuthModalParameter.value.payment.customer?.email) {
          return member.atoneMemberControllerMemberTypePost(notNil(tokens.value.requestToken), {
            mailAddress: atoneAuthModalParameter.value.payment.customer?.email ?? '',
          });
        }
        // payment.customer.emailが設定されてない時は無条件で新規会員になるため、APIを呼ばない。
        // API呼んだ体で新規会員レスポンスを返す。
        return Promise.resolve({
          data: {
            memberType: AtoneMemberTypePostResponseMemberTypeEnum.NUMBER_0,
          },
        });
      },
      {
        enabled: computed(() => needLogin.value),
        onSuccess: ({ data }) => {
          setAtoneMemberTypePostResult(data);
          const { memberType } = data;

          if (typeof memberType === 'number') {
            // メールアドレス使用状況(0:新規会員,1:NP会員,2:atone会員 3:仮会員)でパスを分岐
            nextPage.value = {
              name: ROUTE_NAME.ATONEAUTH_INTRO,
            };
          }
        },
      },
    );

    // フィンガープリント登録API
    const {
      //
      isSuccess: successTrackTokenPost,
    } = useQuery(
      'atone.atoneTrackTokenPost',
      () =>
        tracks.atoneTracksControllerTrackTokenPost(
          getRefDeep(tokens, 'npToken', ''),
          atoneAuthModalParameter.value.pubKey,
          {
            authenticationType: AtoneTracksPostRequestAuthenticationTypeEnum.NUMBER_1,
            fingerPrint: fingerprint.value,
          },
        ),
      {
        enabled: computed(() => fingerprint.resolved && unref(successAtoneAuthenticateGet) && !unref(memberIsGuest)),
        onSuccess: ({ data }) => {
          setAtoneTracksPostResult(data);
        },
      },
    );

    // フィンガープリント存在チェック
    const { isSuccess: successTrackTokenTrust } = useQuery(
      'atone.TrackTokenTrust',
      () =>
        tracks.atoneTracksControllerTrackTokenTrust(
          getRefDeep(atoneTracksPostResult, 'trackToken'),
          getRefDeep(tokens, 'npToken'),
          atoneAuthModalParameter.value.pubKey,
        ),
      {
        enabled: computed(() => unref(successTrackTokenPost)),
        onSuccess: ({ data }) => {
          setAtoneTracksTrustResult(data);

          const { trustedFlag } = data;
          if (trustedFlag) {
            // 認証完了へ遷移する。
            nextPage.value = {
              name: ROUTE_NAME.ATONEAUTH_COMPLETE,
            };
          } else {
            // SMS認証画面へ遷移する。
            nextPage.value = {
              name: ROUTE_NAME.ATONEAUTH_ATONE_MEMBER_SMS_AUTH,
              query: {
                previous: ROUTE_NAME.ATONEAUTH_INTRO,
                logout: 'true',
              },
            };
          }
        },
      },
    );

    // SMS送信。フィンガープリント存在チェックでtrustedでない場合に呼び出す。
    const { isSuccess: successSmsNotifyPost, isError: errorSmsNotifyPost } = useQuery(
      'atone.SmsNotifyPost',
      () =>
        sms.atoneSmsControllerSmsNotifyPost(getRefDeep(tokens, 'npToken'), atoneAuthModalParameter.value.pubKey, {
          trackToken: getRefDeep(atoneTracksPostResult, 'trackToken'),
          resend: false,
        }),
      {
        enabled: computed(() => unref(successTrackTokenTrust) && !(atoneTracksTrustResult.value?.trustedFlag ?? true)),
        onSuccess: ({ data }) => {
          setAtoneSmsNotifyPostResult(data);
        },
      },
    );

    // 認証モーダルではポイントAPI呼ばなくて良くない？？？
    // // ポイント取得
    // const { isSuccess: successAtonePointGet } = useQuery(
    //   'atone.atonePointGet',
    //   () =>
    //     point.atonePointControllerPointGet(
    //       getRefDeep(tokens, 'npToken'),
    //       atoneAuthModalParameter.value.pubKey,
    //       getRefDeep(tokens, 'trustedTrackToken'),
    //     ),
    //   {
    //     enabled: computed(() => unref(successTrackTokenTrust) && (atoneTracksTrustResult.value?.trustedFlag ?? false)),
    //     onSuccess: ({ data }) => {
    //       setAtonePointGetResult(data);
    //     },
    //   },
    // );

    // 進捗バー用。
    const isCompleted = useOrAll(
      successAtoneMemberTypePost, // 非ログイン状態会員判定終了後
      successSmsNotifyPost, // SMS送信後
      successTrackTokenTrust, // フィンガープリント存在チェック後
      // successAtonePointGet, // ポイント取得まで成功
      errorSmsNotifyPost, // SMS送信失敗時
    );
    const isLoading = computed(() => !unref(isCompleted));

    // ローディング終了時の処理
    const onComplete = () => {
      const location = unref(nextPage);
      if (location) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        router.replace(location);
      }
    };

    return readonly(
      reactive({
        title: '',
        duration: 5000,
        onComplete,
        isLoading,
        isCompleted,
      }),
    );
  },
});
